import { useLocale, useTranslate } from '@va/localization';
import { TooltipWrapper } from '@va/ui/tooltips';
import { addNumberSeparator, getPercentageColors } from '@va/util/helpers';
import classNames from 'classnames';
import { FC, useMemo } from 'react';
import { fontWeights, Heading3, Paragraph, paragraphSizes } from '../../typography';

export type ChartCategoryFormat = 'percent' | 'number' | 'currency';

export type ChartCategoryProps = {
  label: string;
  labelTooltip?: string;
  color: string;
  value: number;
  valueDetails?: string;
  percentage: number;
  selected: boolean;
  onClick: () => void;
  format?: ChartCategoryFormat;
  reversePercentageColors?: boolean;
};

/** @deprecated use ChartTile instead */
export const ChartCategory = ({
  label,
  color,
  value,
  valueDetails = '',
  percentage,
  selected,
  labelTooltip,
  onClick,
  format = 'number',
  reversePercentageColors = false,
}: ChartCategoryProps) => {
  const translate = useTranslate();
  const { locale } = useLocale();

  const classes = classNames(
    'text-center shrink-0 grow min-w-150 rounded-xl hover:bg-primary-lighter-06 py-2 cursor-pointer px-2',
    {
      'bg-white text-gray-silver': !selected,
    },
  );

  const percentageClasses = classNames({
    [getPercentageColors(percentage, reversePercentageColors)]: selected,
    'text-gray-silver': !selected,
  });

  const percentageDisplayValue = useMemo(() => {
    if (percentage === 0) return '-';
    return addNumberSeparator(percentage, locale, {
      style: 'percent',
      signDisplay: 'exceptZero',
    });
  }, [percentage, locale]);

  return (
    <div className={classes} onClick={onClick}>
      <div className='flex justify-center items-center mb-6px px-2'>
        <TooltipWrapper interactive disabled={!labelTooltip} content={labelTooltip}>
          <div className='flex w-full justify-center'>
            <Paragraph
              className='truncate'
              size={paragraphSizes.normal}
              weight={fontWeights.medium}
              style={selected ? { color } : undefined}
            >
              {label || 'n/a'}
            </Paragraph>
          </div>
        </TooltipWrapper>
      </div>
      <Heading3 colorClassName={!selected ? 'text-gray-silver' : undefined} weight={fontWeights.medium}>
        <ChartCategoryHeading format={format} value={value} />
      </Heading3>
      <Paragraph weight={fontWeights.medium}>{valueDetails}</Paragraph>
      <TooltipWrapper interactive content={translate('percentComparedToPrevious')}>
        <div className={percentageClasses}>{percentageDisplayValue}</div>
      </TooltipWrapper>
    </div>
  );
};

const ChartCategoryHeading: FC<{
  format: ChartCategoryFormat;
  value: number;
}> = ({ format, value }) => {
  const { locale } = useLocale();

  if (format === 'number') {
    return addNumberSeparator(value, locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      // @ts-ignore
      trailingZeroDisplay: 'stripIfInteger',
    });
  }
  if (format === 'percent') {
    return addNumberSeparator(value, locale, { style: 'percent' });
  }
  if (format === 'currency') {
    return addNumberSeparator(value, locale, { style: 'currency', currency: 'EUR' });
  }
  return value;
};
